import mainStyle from './captivePortal.module.scss';

import { useState, useEffect } from 'react';
import { cloneDeep, isEqual, isNil, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, TextField } from '@mui/material';

import { ReactComponent as BinIcon } from 'assets/images/icons/icn_table_cell_Bin.svg';
import { ReactComponent as BinHoverIcon } from 'assets/images/icons/icn_table_cell_Bin_hover.svg';
import { ReactComponent as CaptivePortalIcon } from 'assets/images/icons/icn_dashboard_captive_portal.svg';

// Utils
import { validUrl } from 'utils/validate';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Components
import TabsField from 'components/Tab';
import CollapseCard from 'components/Collapse';
import GlobalAlert from 'components/Notification';
import { MenuSelect } from 'components/Select/menuSelect';
import { RadioButton } from 'components/buttons/RadioButton';
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';

import Loading from 'pages/System/RealmSetting/components/CaptivePortal/components/Loading';
import UrlInput from 'pages/System/RealmSetting/components/CaptivePortal/components/UrlInput';
import SelectMenu from 'pages/System/RealmSetting/components/CaptivePortal/components/SelectMenu';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const EditCaptivePortalContent = ({
  drawerOpen,
  setDrawerOpen,
  setSelectedRowItem,
  setOpenRemoveProfileModal,
  tableData,
  originalRowData,
  reloadPage,
  initCaptivePortal,
  formData,
  setFormData,
  showEditGeneral,
}) => {
  const theme = useTheme();

  const { auth } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [updateCaptivePortalUuid, _setUpdateCaptivePortalUuid] = useState(uuidv4());

  const [defaultFormData, setDefaultFormData] = useState({});
  const [tabValue, setTabValue] = useState('settings');
  const [hasChanged, setHasChanged] = useState(false);
  const [hasDuplicateName, setHasDuplicateName] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);

  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const textfieldStyle = {
    width: '280px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.customBorderRadius[1],
      height: '30px',
      '& fieldset': {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.mainHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const labelStyle = {
    width: '160px',
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const rowStyle = {
    display: 'flex',
    gap: '18px',
    alignItems: 'center',
    width: '100%',
  };

  const tabData = [
    {
      name: 'Settings',
      value: 'settings',
      disabled: null,
      icon: null,
    },
    {
      name: 'Advanced',
      value: 'advanced',
      disabled: null,
      icon: null,
    },
  ];

  const parseCaptivePortalInfo = isOriginalRowData => {
    const newFormData = cloneDeep(formData);
    const rowData = cloneDeep(isOriginalRowData);

    newFormData['id'] = rowData?.config?.id || '';

    newFormData['profileName'] = rowData?.config?.name || '';

    newFormData['authTypeList'] = newFormData.authTypeList.map(item => ({ ...item, isActive: item.value === rowData?.config?.authType }));

    newFormData['idleTimeoutOption'] = rowData?.config?.idleTimeoutOption;
    if (rowData?.config?.idleTimeoutOption === 'DEFAULT') {
      newFormData['idleTimeoutList'] = newFormData.idleTimeoutList.map(item => ({ ...item, isActive: item.value === rowData?.config?.idleTimeout }));
    } else {
      newFormData['idleTimeoutLabel'] = rowData?.config?.idleTimeout; // CUSTOM
    }

    newFormData['authTimeoutOption'] = rowData?.config?.authTimeoutOption;
    if (rowData?.config?.authTimeoutOption === 'ENABLE') {
      newFormData['authTimeoutList'] = newFormData.authTimeoutList.map(item => ({ ...item, isActive: item.value === rowData?.config?.authTimeout }));
    } else if (rowData?.config?.authTimeoutOption === 'CUSTOM') {
      newFormData['authTimeoutLabel'] = rowData?.config?.authTimeout; // CUSTOM
    } else {
      newFormData['authTimeoutLabel'] = 5; // DISABLE
    }

    newFormData['authPerDayOption'] = rowData?.config?.authPerDayOption;
    if (rowData?.config?.authPerDayOption === 'CUSTOM') {
      newFormData['authPerDayLabel'] = rowData?.config?.authPerDay;
    } else {
      newFormData['authPerDayLabel'] = 5; // UNLIMIT
    }

    newFormData['reAuthIntervalOption'] = rowData?.config?.reAuthIntervalOption;
    if (rowData?.config?.reAuthIntervalOption === 'ENABLE') {
      newFormData['reAuthIntervalList'] = newFormData.reAuthIntervalList.map(item => ({ ...item, isActive: item.value === rowData?.config?.reAuthInterval }));
    } else if (rowData?.config?.reAuthIntervalOption === 'CUSTOM') {
      newFormData['reAuthIntervalLabel'] = rowData?.config?.reAuthInterval; // CUSTOM
    } else {
      newFormData['reAuthIntervalLabel'] = 10; // DISABLE
    }

    newFormData['landingPageOption'] = rowData?.config?.landingPageOption;
    if (rowData?.config?.landingPageOption === 'CUSTOM') {
      newFormData['landingPageUrlProtocol'] = rowData?.config?.landingPageUrl.split('://')?.[0];
      newFormData['landingPageUrlLabel'] = rowData?.config?.landingPageUrl.split('://')?.[1];
    }

    const whitelistId = rowData?.config?.macWhiteList || '';
    newFormData['whitelistList'] = newFormData.whitelistList.map(item => ({ ...item, isActive: item?.value === whitelistId }));

    setFormData(newFormData);
    setDefaultFormData(newFormData);
  };

  const checkConfigInvalid = () => {
    const profileNameInvalid = hasDuplicateName || !formData?.profileName;

    const idleTimeoutInvalid = formData?.idleTimeoutOption === 'CUSTOM' && (formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440);

    const authTimeoutInvalid = formData?.authTimeoutOption === 'CUSTOM' && (formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440);

    const authPerDayInvalid = formData?.authPerDayOption === 'CUSTOM' && (formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720);

    const reAuthIntervalInvalid = formData?.reAuthIntervalOption === 'CUSTOM' && (formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 720);

    const landingPageInvalid = formData?.landingPageOption === 'CUSTOM' && (!formData?.landingPageUrlLabel || !validUrl(formData?.landingPageUrlLabel));

    return profileNameInvalid || idleTimeoutInvalid || authTimeoutInvalid || authPerDayInvalid || reAuthIntervalInvalid || landingPageInvalid;
  };

  const handleChangeProfileName = newValue => {
    setFormData(prev => ({ ...prev, profileName: newValue }));

    const tmpTableData = cloneDeep(tableData)?.filter(item => item?.captivePortalName !== originalRowData?.captivePortalName);
    const hasDuplicate = tmpTableData.some(item => item.captivePortalName === newValue);
    setHasDuplicateName(hasDuplicate);
  };

  const handleChangeItem = (list, newValue) => {
    const newFormData = cloneDeep(formData);
    if (newFormData?.[list]?.length > 0) {
      newFormData[list] = newFormData?.[list]?.map(item => ({
        ...item,
        isActive: item.value === newValue,
      }));
    }
    setFormData(newFormData);
  };

  const handleClickApplyBtn = () => {
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals/:captivePortalId.update`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals/:captivePortalId.update`,
    };

    const apiKey = apiConfig[auth.scopeLevel];

    const restfulParams = standalone
      ? { orgId: auth.realm.id, siteId: auth.sites?.id, captivePortalId: formData?.id || '' }
      : {
          entId: auth.realm.id,
          orgId: auth.realm.id,
          siteId: auth.sites?.id,
          captivePortalId: formData?.id || '',
        };

    const payload = {
      id: formData?.id || '',
      name: formData?.profileName || '',
      authType: formData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH',
      idleTimeoutOption: formData?.idleTimeoutOption || 'DEFAULT',
      authTimeoutOption: formData?.authTimeoutOption || 'DISABLE',
      authPerDayOption: formData?.authPerDayOption || 'CUSTOM',
      reAuthIntervalOption: formData?.reAuthIntervalOption || 'DISABLE',
      landingPageOption: formData?.landingPageOption || 'NO_SPECIFIC',
      macWhiteList: formData?.whitelistList?.find(item => item.isActive)?.value || '',
    };

    if (formData?.idleTimeoutOption === 'DEFAULT') {
      payload['idleTimeout'] = formData?.idleTimeoutList?.find(item => item.isActive)?.value || 60;
    } else {
      payload['idleTimeout'] = formData?.idleTimeoutLabel || 5; // CUSTOM
    }

    if (formData?.authTimeoutOption === 'ENABLE') {
      payload['authTimeout'] = formData?.authTimeoutList?.find(item => item.isActive)?.value || 60;
    } else if (formData?.authTimeoutOption === 'CUSTOM') {
      payload['authTimeout'] = formData?.authTimeoutLabel || 5;
    } else {
      payload['authTimeout'] = 2; // DISABLE
    }

    if (formData?.authPerDayOption === 'CUSTOM') {
      payload['authPerDay'] = formData?.authPerDayLabel || 1;
    } else {
      payload['authPerDay'] = 1; // UNLIMIT
    }

    if (formData?.reAuthIntervalOption === 'ENABLE') {
      payload['reAuthInterval'] = formData?.reAuthIntervalList?.find(item => item.isActive)?.value || 60;
    } else if (formData?.reAuthIntervalOption === 'CUSTOM') {
      payload['reAuthInterval'] = formData?.reAuthIntervalLabel || 10;
    } else {
      payload['reAuthInterval'] = 1; // DISABLE
    }

    if (formData?.landingPageOption === 'CUSTOM') {
      payload['landingPageUrl'] = `${formData?.landingPageUrlProtocol}://${formData?.landingPageUrlLabel}`;
    } else {
      payload['landingPageUrl'] = '';
    }

    sendRequest(apiKey, updateCaptivePortalUuid, restfulParams, payload);
  };

  useEffect(() => {
    if (drawerOpen && originalRowData) {
      setTabValue('settings');
      parseCaptivePortalInfo(originalRowData);
    }
  }, [drawerOpen, originalRowData]);

  useEffect(() => {
    if (!Object.keys(formData).length || !Object.keys(defaultFormData).length) {
      return;
    }

    setHasChanged(!isEqual(formData, defaultFormData));
  }, [formData, defaultFormData]);

  useEffect(() => {
    const isUpdateResponse = getData(updateCaptivePortalUuid);
    setIsLoading(false);
    if (!isUpdateResponse) return;

    if (checkMockup) {
      if (isUpdateResponse && isUpdateResponse?.data) {
        reloadPage(isUpdateResponse.data);
        setDrawerOpen(false);
        setSelectedRowItem(null);
      }
    } else {
      if (isUpdateResponse) {
        if (isUpdateResponse.hasOwnProperty('code') && isUpdateResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isUpdateResponse?.message,
          });
        } else {
          initCaptivePortal();
          setDrawerOpen(false);
          setSelectedRowItem(null);
        }
      }
    }
  }, [dataMap]);

  return (
    <Box sx={{ maxWidth: '600px', height: '120vh', position: 'relative' }}>
      {isLoading && <Loading />}

      <Box sx={{ display: 'flex', paddingBottom: 2, px: 4 }}>
        <CaptivePortalIcon />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            marginLeft: 1,
            overflowWrap: 'anywhere',
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: '20px', color: '#373737' }}>{originalRowData?.captivePortalName}</Box>
          <Box>{originalRowData?.authTypeLabel}</Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <TabsField
          tabsStyles={{
            '& .MuiButtonBase-root': {
              margin: '0 20px',
            },
          }}
          tabVal={tabValue}
          handleTabsChange={(e, newValue) => setTabValue(newValue)}
          tabData={tabData}
        />
      </Box>

      {/* Tab - Settings */}
      {tabValue === 'settings' && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            px: 4,
            boxSizing: 'border-box',
            gap: '20px',
          }}
        >
          {/* General */}
          <CollapseCard title="General">
            <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1 }}>
              {/* Profile Name */}
              <Box sx={rowStyle}>
                <Box sx={{ ...labelStyle, paddingTop: hasDuplicateName ? '0px' : '10px', height: hasDuplicateName ? '52px' : '50px' }}>
                  <Typography className={mainStyle['collapse-typography']}>Profile Name</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <TextField
                    placeholder="e.g. Pro WiFi Access"
                    sx={textfieldStyle}
                    autoComplete="off"
                    inputProps={{
                      minLength: 1,
                      maxLength: 64,
                    }}
                    value={formData?.profileName || ''}
                    onChange={e => handleChangeProfileName(e.target.value)}
                    error={hasDuplicateName || !formData?.profileName}
                    helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
                    disabled={!showEditGeneral}
                  />
                </Box>
              </Box>

              {/* Authentication Type */}
              <Box sx={{ ...rowStyle, marginTop: 2 }}>
                <Box sx={labelStyle}>
                  <Typography className={mainStyle['collapse-typography']}>Authentication Type</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <MenuSelect
                    width={textfieldStyle.width}
                    value={formData?.authTypeList?.find(item => item.isActive)?.value}
                    options={formData?.authTypeList}
                    onChange={obj => handleChangeItem('authTypeList', obj.value)}
                    disabled={!showEditGeneral}
                  />
                </Box>
              </Box>

              {/* Idle Timeout */}
              <Box sx={{ ...rowStyle, alignItems: 'flex-start', marginTop: 1 }}>
                <Box
                  sx={{
                    ...labelStyle,
                    marginTop: '6px',
                    height: formData?.idleTimeoutOption === 'DEFAULT' ? '20px' : '50px',
                  }}
                >
                  <Typography className={mainStyle['collapse-typography']}>Idle Timeout</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <Box style={{ display: 'flex' }}>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Default"
                        name="idleTimeoutOption"
                        value={'DEFAULT'}
                        checked={formData?.idleTimeoutOption === 'DEFAULT'}
                        onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'DEFAULT' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Custom"
                        name="idleTimeoutOption"
                        value={'CUSTOM'}
                        checked={formData?.idleTimeoutOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'CUSTOM' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                  </Box>
                  {formData?.idleTimeoutOption === 'DEFAULT' && (
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.idleTimeoutList?.find(item => item.isActive)?.value}
                      options={formData?.idleTimeoutList}
                      onChange={obj => handleChangeItem('idleTimeoutList', obj.value)}
                      disabled={!showEditGeneral}
                    />
                  )}
                  {formData?.idleTimeoutOption === 'CUSTOM' && (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '-110px',
                          top: '5px',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Min(s)
                      </Box>

                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="2"
                        max="1440"
                        value={formData?.idleTimeoutLabel}
                        onChange={e => setFormData(prev => ({ ...prev, idleTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440}
                        disabled={!showEditGeneral}
                      />
                      <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Authentication Timeout */}
              <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    ...labelStyle,
                    marginTop: '6px',
                    height: formData?.authTimeoutOption === 'DISABLE' ? '20px' : '50px',
                  }}
                >
                  <Typography className={mainStyle['collapse-typography']}>Authentication Timeout</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Disabled"
                        name="authTimeoutOption"
                        value={'DISABLE'}
                        checked={formData?.authTimeoutOption === 'DISABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'DISABLE' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Enabled"
                        name="authTimeoutOption"
                        value={'ENABLE'}
                        checked={formData?.authTimeoutOption === 'ENABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'ENABLE' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Custom"
                        name="authTimeoutOption"
                        value={'CUSTOM'}
                        checked={formData?.authTimeoutOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'CUSTOM' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                  </Box>

                  {formData?.authTimeoutOption === 'ENABLE' && (
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.authTimeoutList?.find(item => item.isActive)?.value}
                      options={formData?.authTimeoutList}
                      onChange={obj => handleChangeItem('authTimeoutList', obj.value)}
                      disabled={!showEditGeneral}
                    />
                  )}
                  {formData?.authTimeoutOption === 'CUSTOM' && (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '-110px',
                          top: '5px',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Min(s)
                      </Box>
                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="2"
                        max="1440"
                        value={formData?.authTimeoutLabel}
                        onChange={e => setFormData(prev => ({ ...prev, authTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440}
                        disabled={!showEditGeneral}
                      />
                      <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Authentication Per Day */}
              <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    ...labelStyle,
                    marginTop: '6px',
                    height: formData?.authPerDayOption === 'CUSTOM' ? '20px' : '50px',
                  }}
                >
                  <Typography className={mainStyle['collapse-typography']}>Authentication Per Day</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Custom"
                        name="authPerDayOption"
                        value={'CUSTOM'}
                        checked={formData?.authPerDayOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'CUSTOM' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Unlimited"
                        name="authPerDayOption"
                        value={'UNLIMIT'}
                        checked={formData?.authPerDayOption === 'UNLIMIT'}
                        onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'UNLIMIT' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                  </Box>

                  {formData?.authPerDayOption === 'CUSTOM' && (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '-110px',
                          top: '5px',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Time/Day
                      </Box>
                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="1"
                        max="720"
                        value={formData?.authPerDayLabel}
                        onChange={e => setFormData(prev => ({ ...prev, authPerDayLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720}
                        disabled={!showEditGeneral}
                      />

                      <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    ...labelStyle,
                    marginTop: '10px',
                    height: formData?.reAuthIntervalOption === 'DISABLE' ? '20px' : '50px',
                  }}
                >
                  <Typography className={mainStyle['collapse-typography']}>Re-Authentication Interval</Typography>
                </Box>
                <Box sx={labelStyle}>
                  <Box style={{ display: 'flex' }}>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Disabled"
                        name="reAuthIntervalOption"
                        value={'DISABLE'}
                        checked={formData?.reAuthIntervalOption === 'DISABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'DISABLE' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Enabled"
                        name="reAuthIntervalOption"
                        value={'ENABLE'}
                        checked={formData?.reAuthIntervalOption === 'ENABLE'}
                        onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'ENABLE' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                    <Box sx={{ minWidth: '100px' }}>
                      <RadioButton
                        label="Custom"
                        name="reAuthIntervalOption"
                        value={'CUSTOM'}
                        checked={formData?.reAuthIntervalOption === 'CUSTOM'}
                        onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'CUSTOM' }))}
                        disabled={!showEditGeneral}
                      />
                    </Box>
                  </Box>

                  {formData?.reAuthIntervalOption === 'ENABLE' && (
                    <MenuSelect
                      width={textfieldStyle.width}
                      value={formData?.reAuthIntervalList?.find(item => item.isActive)?.value}
                      options={formData?.reAuthIntervalList}
                      onChange={obj => handleChangeItem('reAuthIntervalList', obj.value)}
                      disabled={!showEditGeneral}
                    />
                  )}
                  {formData?.reAuthIntervalOption === 'CUSTOM' && (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '-110px',
                          top: '5px',
                          color: '#aaa',
                          pointerEvents: 'none',
                        }}
                      >
                        Min(s)
                      </Box>
                      <TextField
                        sx={textfieldStyle}
                        autoComplete="off"
                        type="number"
                        min="1"
                        max="720"
                        value={formData?.reAuthIntervalLabel}
                        onChange={e => setFormData(prev => ({ ...prev, reAuthIntervalLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                        error={formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 720}
                        disabled={!showEditGeneral}
                      />
                      <Typography sx={{ marginTop: 1, color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </CollapseCard>

          {/* Remove Profile Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <Button
              onMouseEnter={() => setIsDeleteHovered(true)}
              onMouseLeave={() => setIsDeleteHovered(false)}
              startIcon={isDeleteHovered ? <BinHoverIcon /> : <BinIcon />}
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                backgroundColor: theme.palette.primary.transparent,
                '&:hover': {
                  backgroundColor: theme.palette.primary.transparent,
                  color: theme.palette.primary.mainHover,
                },
                '&:active': {
                  backgroundColor: theme.palette.primary.transparent,
                  color: theme.palette.primary.main,
                },
                '&:disabled': {
                  color: theme.palette.action.disabled,
                },
              }}
              onClick={() => setOpenRemoveProfileModal(true)}
              disabled={!showEditGeneral}
            >
              Remove Profile
            </Button>
          </Box>
        </Box>
      )}

      {/* Tab - Advanced */}
      {tabValue === 'advanced' && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            px: 4,
            boxSizing: 'border-box',
            gap: '20px',
          }}
        >
          {/* Landing Page */}
          <CollapseCard title="Landing Page">
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography className={mainStyle['collapse-typography']}>Clients will be directed to the page after they pass Portal authentication.</Typography>

              <Box sx={{ display: 'grid', marginLeft: '10px' }}>
                <RadioButton
                  label="Not Specified"
                  name="landingPageOption"
                  value={'NO_SPECIFIC'}
                  checked={formData?.landingPageOption === 'NO_SPECIFIC'}
                  onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'NO_SPECIFIC' }))}
                  disabled={!showEditGeneral}
                />
                <RadioButton
                  label="The Promotional URL"
                  name="landingPageOption"
                  value={'CUSTOM'}
                  checked={formData?.landingPageOption === 'CUSTOM'}
                  onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'CUSTOM' }))}
                  disabled={!showEditGeneral}
                />
                {formData?.landingPageOption === 'CUSTOM' && (
                  <Box sx={{ width: '200px', marginBottom: '10px' }}>
                    <UrlInput
                      maxLength={formData?.landingPageUrlProtocol === 'https' ? 2075 : 2076} // Custom landing page URL max 2083 characters.
                      placeholder="www.dlink.com"
                      protocol={formData?.landingPageUrlProtocol}
                      onChangeProtocol={protocol => setFormData(prev => ({ ...prev, landingPageUrlProtocol: protocol }))}
                      inputValue={formData?.landingPageUrlLabel || ''}
                      onChangeInputValue={newUrlInputValue => setFormData(prev => ({ ...prev, landingPageUrlLabel: newUrlInputValue }))}
                      disabled={!showEditGeneral}
                      error={
                        (!isNil(formData?.landingPageUrlLabel) && size(formData?.landingPageUrlLabel) === 0) ||
                        (size(formData?.landingPageUrlLabel) > 0 && !validUrl(formData?.landingPageUrlLabel))
                      }
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </CollapseCard>

          {/* Whitelist */}
          <CollapseCard title="Whitelist">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <>
                <Typography className={mainStyle['collapse-typography']} sx={{ whiteSpace: 'normal' }}>
                  To set up the Whitelist, add one or more existing MAC Address Profiles. A maximum of 64 profiles can be configured.
                </Typography>

                <Box sx={{ marginBottom: 1 }}>
                  <SelectMenu menuList={formData?.whitelistList} onChangeItem={newValue => handleChangeItem('whitelistList', newValue)} disabled={!showEditGeneral} />
                </Box>
              </>
            </Box>
          </CollapseCard>
        </Box>
      )}

      {hasChanged && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '15px',
            right: '30px',
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'primary.lightBlue',
              width: '530px',
              height: '64px',
              gap: 2,
            }}
          >
            <OutlineButton
              width="100px"
              height="32px"
              onClick={() => {
                setFormData(defaultFormData); // Reset
              }}
            >
              Cancel
            </OutlineButton>
            <SolidButton width="100px" height="32px" onClick={handleClickApplyBtn} disabled={!showEditGeneral || checkConfigInvalid()}>
              Apply
            </SolidButton>
          </Box>
        </Box>
      )}

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};
export default EditCaptivePortalContent;
